/**
 * AnimatedWeatherIcon — flat animated SVG weather icon.
 *
 * Renders a colourful, framer-motion powered SVG that adapts to the current
 * WMO code and wind speed. Designed to sit on a gradient tile background:
 * shapes are opaque (no lucide outlines) so they read well against colour.
 *
 * Variants (auto-picked from weather_code + wind speed):
 *   sun            – bright sun with rotating rays
 *   sunWind        – sun with sweeping wind gusts (windSpeed > 20 km/h & code 0)
 *   partlyCloudy   – sun peeking behind a drifting cloud (WMO 1-2)
 *   cloudy         – layered clouds gently bobbing (WMO 3)
 *   thunderstorm   – dark cloud with flashing lightning (WMO 95-99)
 *   rain           – cloud with falling raindrops (WMO 51-82)
 *   snow           – cloud with drifting snowflakes (WMO 71-86)
 *   fog            – horizontal fog lines flowing sideways (WMO 45-48)
 *
 * Props:
 *   code       – Open-Meteo WMO code (number)
 *   windSpeed  – current wind speed in km/h (number, optional)
 *   size       – pixel size (default 72)
 */
import { motion } from "framer-motion";

const pickVariant = (code, windSpeed = 0) => {
  const c = Number(code);
  if (c === 0) return windSpeed > 20 ? "sunWind" : "sun";
  if ([1, 2].includes(c)) return "partlyCloudy";
  if (c === 3) return "cloudy";
  if ([45, 48].includes(c)) return "fog";
  if ([51, 53, 55, 61, 63, 65, 80, 81, 82].includes(c)) return "rain";
  if ([71, 73, 75, 77, 85, 86].includes(c)) return "snow";
  if ([95, 96, 99].includes(c)) return "thunderstorm";
  return "cloudy";
};

// Reusable primitives ────────────────────────────────────────────────────────
const Cloud = ({ x = 0, y = 0, fill = "#FFFFFF", stroke = "#E5E7EB", scale = 1, shadow = true }) => (
  <g transform={`translate(${x} ${y}) scale(${scale})`}>
    {shadow && (
      <ellipse cx="50" cy="70" rx="34" ry="4" fill="rgba(15,23,42,0.18)" />
    )}
    <path
      d="M28,64 C15,64 10,54 15,46 C10,36 21,28 30,32 C33,20 51,18 57,29 C68,26 78,34 78,44 C86,45 88,58 78,64 Z"
      fill={fill}
      stroke={stroke}
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </g>
);

const Sun = ({ cx = 50, cy = 40, r = 14, rayLength = 8, spin = true }) => (
  <g>
    {/* Rays — rotate as one group */}
    <motion.g
      animate={spin ? { rotate: 360 } : {}}
      transition={spin ? { duration: 18, repeat: Infinity, ease: "linear" } : {}}
      style={{ transformOrigin: `${cx}px ${cy}px` }}
    >
      {Array.from({ length: 8 }).map((_, i) => {
        const angle = (i * 45 * Math.PI) / 180;
        const x1 = cx + Math.cos(angle) * (r + 3);
        const y1 = cy + Math.sin(angle) * (r + 3);
        const x2 = cx + Math.cos(angle) * (r + 3 + rayLength);
        const y2 = cy + Math.sin(angle) * (r + 3 + rayLength);
        return (
          <line
            key={i}
            x1={x1} y1={y1} x2={x2} y2={y2}
            stroke="#FDE047"
            strokeWidth="3.4"
            strokeLinecap="round"
          />
        );
      })}
    </motion.g>
    {/* Pulsing halo */}
    <motion.circle
      cx={cx} cy={cy} r={r + 4}
      fill="#FEF3C7"
      animate={{ opacity: [0.35, 0.6, 0.35], scale: [1, 1.08, 1] }}
      transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
      style={{ transformOrigin: `${cx}px ${cy}px` }}
    />
    {/* Sun body */}
    <circle cx={cx} cy={cy} r={r} fill="#FBBF24" />
    <circle cx={cx - 3} cy={cy - 4} r={r * 0.35} fill="#FDE68A" opacity="0.9" />
  </g>
);

// Wind gust — three swept lines flowing left→right
const WindLines = ({ y = 60, colour = "#F1F5F9" }) => (
  <g>
    {[0, 1, 2].map((i) => (
      <motion.path
        key={i}
        d={`M8,${y + i * 8} Q30,${y + i * 8 - 3} 50,${y + i * 8} T92,${y + i * 8}`}
        stroke={colour}
        strokeWidth={i === 1 ? 4 : 3}
        strokeLinecap="round"
        fill="none"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: [0, 1, 1], opacity: [0, 1, 0], x: [-20, 0, 20] }}
        transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.35, ease: "easeInOut" }}
      />
    ))}
  </g>
);

const Drop = ({ x, delay = 0, colour = "#60A5FA" }) => (
  <motion.path
    d={`M${x},58 Q${x - 2},63 ${x},68 Q${x + 2},63 ${x},58 Z`}
    fill={colour}
    initial={{ y: -12, opacity: 0 }}
    animate={{ y: [0, 16, 22], opacity: [0, 1, 0] }}
    transition={{ duration: 1.2, repeat: Infinity, delay, ease: "easeIn" }}
  />
);

const Flake = ({ x, delay = 0 }) => (
  <motion.g
    initial={{ y: -8, opacity: 0 }}
    animate={{ y: [0, 20, 26], opacity: [0, 1, 0], rotate: [0, 180, 360] }}
    transition={{ duration: 2.2, repeat: Infinity, delay, ease: "easeIn" }}
    style={{ transformOrigin: `${x}px 66px` }}
  >
    <g transform={`translate(${x} 66)`}>
      <line x1="-4" y1="0" x2="4" y2="0" stroke="#E0F2FE" strokeWidth="1.6" strokeLinecap="round" />
      <line x1="0" y1="-4" x2="0" y2="4" stroke="#E0F2FE" strokeWidth="1.6" strokeLinecap="round" />
      <line x1="-3" y1="-3" x2="3" y2="3" stroke="#E0F2FE" strokeWidth="1.4" strokeLinecap="round" />
      <line x1="-3" y1="3" x2="3" y2="-3" stroke="#E0F2FE" strokeWidth="1.4" strokeLinecap="round" />
    </g>
  </motion.g>
);

// ── Variant renderers ───────────────────────────────────────────────────────
const V = {
  sun: () => <Sun cx={50} cy={50} r={16} rayLength={9} />,

  sunWind: () => (
    <>
      <Sun cx={38} cy={38} r={13} rayLength={7} />
      <WindLines y={62} colour="#F1F5F9" />
    </>
  ),

  partlyCloudy: () => (
    <>
      <Sun cx={32} cy={32} r={12} rayLength={6} />
      <motion.g
        animate={{ x: [0, 4, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      >
        <Cloud x={22} y={18} scale={0.75} fill="#FFFFFF" stroke="#E2E8F0" />
      </motion.g>
    </>
  ),

  cloudy: () => (
    <>
      <motion.g
        animate={{ x: [-2, 2, -2] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      >
        <Cloud x={-4} y={4} scale={0.75} fill="#CBD5E1" stroke="#94A3B8" shadow={false} />
      </motion.g>
      <motion.g
        animate={{ x: [2, -2, 2] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      >
        <Cloud x={6} y={0} scale={0.9} fill="#FFFFFF" stroke="#CBD5E1" />
      </motion.g>
    </>
  ),

  fog: () => (
    <>
      <Cloud x={2} y={-4} scale={0.85} fill="#F1F5F9" stroke="#CBD5E1" shadow={false} />
      {[0, 1, 2, 3].map((i) => (
        <motion.line
          key={i}
          x1="10" y1={64 + i * 6}
          x2="90" y2={64 + i * 6}
          stroke="#94A3B8"
          strokeWidth={i % 2 === 0 ? 3.5 : 2.4}
          strokeLinecap="round"
          animate={{ x: [i % 2 === 0 ? -8 : 8, i % 2 === 0 ? 8 : -8] }}
          transition={{ duration: 3 + i * 0.4, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
        />
      ))}
    </>
  ),

  rain: () => (
    <>
      <Cloud x={2} y={-2} scale={0.9} fill="#94A3B8" stroke="#64748B" shadow={false} />
      <Drop x={30} delay={0} />
      <Drop x={45} delay={0.35} />
      <Drop x={60} delay={0.7} />
      <Drop x={72} delay={0.15} />
      <Drop x={38} delay={0.55} colour="#93C5FD" />
    </>
  ),

  snow: () => (
    <>
      <Cloud x={2} y={-2} scale={0.9} fill="#CBD5E1" stroke="#94A3B8" shadow={false} />
      <Flake x={28} delay={0} />
      <Flake x={45} delay={0.5} />
      <Flake x={62} delay={1.0} />
      <Flake x={76} delay={0.3} />
    </>
  ),

  thunderstorm: () => (
    <>
      <Cloud x={2} y={-2} scale={0.95} fill="#475569" stroke="#334155" shadow={false} />
      <motion.path
        d="M50,52 L42,72 L50,72 L44,90 L60,66 L52,66 L58,52 Z"
        fill="#FACC15"
        stroke="#F59E0B"
        strokeWidth="1.2"
        strokeLinejoin="round"
        animate={{ opacity: [1, 0.15, 1, 1], scale: [1, 1.08, 1, 1] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", times: [0, 0.15, 0.3, 1] }}
        style={{ transformOrigin: "50px 70px" }}
      />
      {/* Small extra rain drops for atmosphere */}
      <Drop x={26} delay={0.2} colour="#93C5FD" />
      <Drop x={74} delay={0.6} colour="#93C5FD" />
    </>
  ),
};

export default function AnimatedWeatherIcon({ code, windSpeed = 0, size = 72, testId }) {
  const variant = pickVariant(code, windSpeed);
  const Renderer = V[variant] || V.cloudy;

  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      data-testid={testId || `animated-weather-${variant}`}
      style={{ filter: "drop-shadow(0 6px 12px rgba(15,23,42,0.20))" }}
      role="img"
      aria-hidden="true"
    >
      <Renderer />
    </svg>
  );
}

export { pickVariant };
